import type { ParsedResult } from 'chrono-node'
import type { StartPeriod } from '../../db/models'
import { startOfDay } from '../../date/calendar'
import { endsWord, startsWord, touchesHyphen, wordsOf } from '../boundaries'
import type { LanguagePack } from '../languages/types'
import type { Candidate, Recognizer, WhenValue } from './types'

const pad = (n: number) => String(n).padStart(2, '0')

const CERTAIN_PARTS = ['year', 'month', 'day', 'weekday', 'hour', 'minute'] as const

/** Eine Zahl oder Spanne ohne alles, was sie zur Uhrzeit macht: „1 bis 3“, „von 10“, „9 to 5“. */
const BARE_NUMBERS = /^(?:von|from\s+)?\s*\d{1,2}(?:\s*(?:bis|to|-|–)\s*\d{1,2})?$/iu

/** Die erste Tageszeit unter den Wörtern des Treffers („heute Abend“ → Abend). */
function periodIn(text: string, lang: LanguagePack): StartPeriod | null {
  for (const word of text.toLowerCase().split(/[^\p{L}]+/u)) {
    const period = lang.periodWords[word]
    if (period) return period
  }
  return null
}

/**
 * **Ein Treffer von chrono-node, der wirklich ein Termin ist?** Gemessen an echten Titeln (02.10.2026)
 * fing chrono-node zu viel: „Fr. Müller“ (Freitag), „Das ist so wichtig“ (Sonntag), „To-Do“
 * (Donnerstag), „review march numbers“ (März 2027), „Abend mit Anna planen“ (heute 18 Uhr).
 */
export function isAppointment(text: string, start: number, result: ParsedResult, lang: LanguagePack): boolean {
  const end = start + result.text.length
  if (!startsWord(text, start) || !endsWord(text, end) || touchesHyphen(text, { start, end })) return false
  // Eine relative Angabe, an der nichts sicher ist, rät chrono-node nur aus — „letzten Quartal“ wurde so zu
  // morgen (gefunden im E2E, 03.10.2026). Feste Ausdrücke wie „morgen“ sind ebenso unsicher, aber gemeint.
  if (result.tags().has('result/relativeDate') && !CERTAIN_PARTS.some(part => result.start.isCertain(part))) return false
  const hasHour = result.start.isCertain('hour')
  const words = result.text.toLowerCase().replace(/\.$/, '').split(/\s+/).filter(w => !lang.prepositions.includes(w))
  // Kurze Wochentage nur mit Uhrzeit: „Do 14:30“ ja, „Do“ allein nein.
  if (!hasHour && words.length === 1 && lang.weekdayShort.includes(words[0]!)) return false
  // Ein Monat allein ist kein Tag: „march“, „Mai“.
  if (!hasHour && result.start.isCertain('month') && !result.start.isCertain('day') && !result.start.isCertain('weekday')) return false
  // Eine Tageszeit als Nomen allein ist kein Termin — „heute Abend“ schon.
  if (words.length === 1 && lang.periodNouns.includes(words[0]!)) return false
  // Nackte Zahlen sind keine Uhrzeit: „Seite 1 bis 3“, „Folge 3 von 10“, „chapters 1 to 3“; dafür braucht
  // es „Uhr“, „um“, am/pm oder Minuten. Und „1:1“ ist ein Verhältnis (Code-Review 03.10.2026).
  if (BARE_NUMBERS.test(result.text.trim())) return false
  // Zahl mit EINEM Buchstaben liest chrono-node als Zeitabstand („5m“, „3D“) — gemeint sind Meter, 3D-Druck.
  if (/^\d+\s?\p{L}$/u.test(result.text.trim())) return false
  if (/\d:\d(?!\d)/u.test(result.text)) return false
  return true
}

/**
 * **Vergangenes ist kein Termin** für eine Aufgabe, die gerade entsteht: „aus dem letzten Quartal“,
 * „von gestern“ (gefunden im E2E, 03.10.2026). Heute zählt — auch mit einer Uhrzeit, die schon vorbei ist.
 */
export function isPast(value: { date: Date }, now: Date): boolean {
  return value.date < startOfDay(now)
}

/** Datum (ohne Uhrzeit), Uhrzeit ODER Tageszeit — wie im Datumsfeld. */
export function whenValueOf(result: ParsedResult, lang: LanguagePack): WhenValue {
  const at = result.start.date()
  const hasHour = result.start.isCertain('hour')
  // Eine getippte Uhrzeit ist genauer als die Tageszeit daneben: „morgen Abend um 20 Uhr“ → 20:00.
  const period = hasHour ? null : periodIn(result.text, lang)
  const time = hasHour ? `${pad(at.getHours())}:${pad(at.getMinutes())}` : null
  const dateKnown = result.start.isCertain('day') || result.start.isCertain('weekday') || result.start.isCertain('month')
  let rangeMinutes: number | undefined
  if (hasHour && result.end?.isCertain('hour')) {
    const minutes = Math.round((result.end.date().getTime() - at.getTime()) / 60_000)
    if (minutes > 0) rangeMinutes = minutes
  }
  return { date: startOfDay(at), time, period, dateKnown, ...(rangeMinutes ? { rangeMinutes } : {}) }
}

/**
 * **Wann** — Datum, Uhrzeit, Tageszeit, Spanne. Steht schon eine Wiederholung da, bestimmt sie den
 * Tag; dann zählt hier nur noch eine Uhrzeit oder Tageszeit („jeden Montag 9 Uhr“).
 */
export const recognizeWhen: Recognizer = ({ text, masked, options, lang, accepted }) => {
  const recurring = accepted.some(c => c.kind === 'recurrence')
  const found: Candidate[] = []
  for (const result of lang.chrono.parse(masked, options.now, { forwardDate: true })) {
    if (!isAppointment(text, result.index, result, lang)) continue
    const value = whenValueOf(result, lang)
    if (isPast(value, options.now)) continue
    if (recurring && value.dateKnown) continue
    const end = result.index + result.text.length
    found.push({ kind: 'when', start: result.index, end, text: text.slice(result.index, end), value })
  }
  if (found.length > 0) return found

  // Eine Tageszeit allein („abends kochen“) gilt für heute — chrono-node kennt sie nicht.
  for (const word of wordsOf(masked)) {
    const core = word.text.replace(/[.,;:!?]+$/u, '')
    const period = lang.periodAdverbs[core.toLowerCase()]
    const end = word.start + core.length
    if (!period || !startsWord(text, word.start) || !endsWord(text, end)) continue
    return [{
      kind: 'when', start: word.start, end, text: core,
      value: { date: startOfDay(options.now), time: null, period, dateKnown: false },
    }]
  }
  return []
}
